import PropTypes from 'prop-types'
import React, {useMemo, useState, useRef} from 'react'
import {Box, useChakraContext, mergeRefs} from '@chakra-ui/react3'
import useOcapiSite from '../../../hooks/useOcapiSite'
import {getServiceForUrl} from '../../../utils/image-services'
import {
    getBucketedWidths,
    getSizesAttribute,
    snapToImageBucket
} from '../../../utils/responsive-image'
import {breakpoints} from '../../theme/breakpoints'
import useResizeObserver from '../../../hooks/use-resize-observer'
import {RESPONSIVE_IMAGE_SIZE_THRESHOLD, DEFAULT_RESPONSIVE_IMAGE_WIDTH} from '../../../constants'
import {createPropsComparator} from '../../../utils/react-memo-helpers'

/**
 * Picture element with responsive images using DIS or Brightcove CDN
 * Supports automatic format conversion (AVIF, WebP) and width bucketing
 *
 * @param {Object} props
 * @param {string} props.src - Image URL (Stokke static, DIS, or Brightcove CDN)
 * @param {string} props.alt - Alt text (required)
 * @param {string} [props.title] - Optional title
 * @param {('eager'|'lazy')} [props.loading='lazy'] - Loading strategy
 * @param {('high'|'low'|'auto')} [props.fetchpriority] - Fetch priority
 * @param {Array|Object} [props.widths] - Responsive widths for srcset
 * @param {Array<{src: string, breakpoint?: string}>} [props.sources] - Breakpoint-specific sources
 * @returns {JSX.Element|null}
 */
const DisImage = ({
    src,
    alt,
    title,
    loading = 'lazy',
    fetchpriority,
    widths,
    sources,
    objectFit,
    imgRef,
    ...rest
}) => {
    const sys = useChakraContext()
    const {sharedLibraryStatic, sharedLibraryDis} = useOcapiSite()

    // Client-side measurement for sizes optimization
    // Callback ref into state so the observer effect re-runs once the <img> element is attached
    const [imgEl, setImgEl] = useState(null)
    const composedImgRef = useMemo(() => mergeRefs(imgRef, setImgEl), [imgRef])

    const [clientSizesPx, setClientSizesPx] = useState(null)

    // Get service for main src - computed once and cached
    const mainService = useMemo(() => {
        return getServiceForUrl(src, {sharedLibraryStatic, sharedLibraryDis})
    }, [src, sharedLibraryStatic, sharedLibraryDis])

    // Cache service context to avoid recreating on every callback
    const serviceContext = useMemo(
        () => ({sharedLibraryStatic, sharedLibraryDis}),
        [sharedLibraryStatic, sharedLibraryDis]
    )

    // Cache service instances per currentSrc URL to avoid repeated getServiceForUrl calls
    const serviceCacheRef = useRef(new Map())

    // Create stable callback that uses cached service instances
    const resizeCallback = useMemo(
        () => (entries) => {
            if (!imgEl || !entries || entries.length === 0) return

            // Use pre-measured width from ResizeObserver to avoid forcing layout.
            // Reading imgEl.clientWidth would trigger synchronous reflow if pending DOM changes exist.
            // contentRect.width is already measured by the observer with no layout cost.
            const measured = entries[0].contentRect.width // CSS px; DPR handled by browser via srcset
            const desiredBucket = snapToImageBucket(measured)
            if (desiredBucket == null) return

            const hasStartedLoading = imgEl.currentSrc || imgEl.complete || imgEl.naturalWidth > 0

            if (hasStartedLoading) {
                // Image has started or completed loading

                if (!imgEl.complete) {
                    // Still loading - safe to update, browser will cancel and switch to better size
                    setClientSizesPx(`${measured}px`)
                    return
                }

                // Image finished loading - check if update is worthwhile
                // Try naturalWidth first (simpler, density-corrected, works across services)
                // Fallback to URL parsing if naturalWidth is unavailable (browser bugs)
                let loadedCssWidth = null

                if (imgEl.naturalWidth > 0) {
                    // naturalWidth is already in CSS pixels and density-corrected
                    loadedCssWidth = imgEl.naturalWidth
                } else {
                    // Fallback: Parse URL to extract width
                    // Use cached service instance to avoid repeated getServiceForUrl calls
                    const currentSrc = imgEl.currentSrc
                    let currentSrcService

                    // Check if we've already looked up this URL (including null results)
                    if (serviceCacheRef.current.has(currentSrc)) {
                        currentSrcService = serviceCacheRef.current.get(currentSrc)
                    } else {
                        // Not in cache - get service and cache it (even if null)
                        currentSrcService = getServiceForUrl(currentSrc, serviceContext)
                        serviceCacheRef.current.set(currentSrc, currentSrcService)
                    }

                    if (currentSrcService) {
                        const loadedPhysicalWidth =
                            currentSrcService.extractWidthFromUrl(currentSrc)
                        if (loadedPhysicalWidth) {
                            // Convert physical pixels to CSS pixels using DPR
                            const dpr = window.devicePixelRatio || 1
                            loadedCssWidth = loadedPhysicalWidth / dpr
                        }
                    }
                }

                if (loadedCssWidth) {
                    const loadedBucket = snapToImageBucket(loadedCssWidth)

                    // If loaded image is larger or equal, keep it (avoid unnecessary refetch)
                    if (loadedBucket >= desiredBucket) {
                        return
                    }

                    // If loaded image is significantly smaller (< 75% of desired),
                    // update to get better quality
                    if (loadedBucket < desiredBucket * RESPONSIVE_IMAGE_SIZE_THRESHOLD) {
                        setClientSizesPx(`${measured}px`)
                        return
                    }

                    // Loaded image is close enough - keep it
                    return
                }

                // Couldn't determine loaded size - be conservative, don't update
                return
            }

            // Image hasn't started loading - safe to update
            setClientSizesPx(`${measured}px`)
        },
        [imgEl, serviceContext]
    )

    useResizeObserver(imgEl, resizeCallback, {debounceMs: 100})

    const effectiveSizes = (defaultSizes) => clientSizesPx ?? defaultSizes

    // Process breakpoint-based sources
    const processedSources = useMemo(() => {
        if (!sources || !Array.isArray(sources) || sources.length === 0) {
            // Use main src as single source
            if (!mainService) return []

            return [
                {
                    service: mainService,
                    src: src
                }
            ]
        }

        const breakpointValues = sys.breakpoints.values || []
        const breakpointMap = Object.fromEntries(breakpointValues.map((bp) => [bp.name, bp.min]))

        return sources
            .map((source) => {
                const service = getServiceForUrl(source.src, {
                    sharedLibraryStatic,
                    sharedLibraryDis
                })

                return {
                    src: source.src,
                    breakpoint: source.breakpoint,
                    service
                }
            })
            .sort((a, b) => {
                // Sort by breakpoint size (largest first)
                const aHasBreakpoint = a.breakpoint && a.breakpoint !== 'base'
                const bHasBreakpoint = b.breakpoint && b.breakpoint !== 'base'

                if (!aHasBreakpoint && !bHasBreakpoint) return 0
                if (!aHasBreakpoint) return 1 // No breakpoint = fallback, goes last
                if (!bHasBreakpoint) return -1

                const aMin = breakpointMap[a.breakpoint] || '0px'
                const bMin = breakpointMap[b.breakpoint] || '0px'
                return parseInt(bMin) - parseInt(aMin) // Larger first
            })
    }, [sources, mainService, src, sys.breakpoints.values, sharedLibraryStatic, sharedLibraryDis])

    // Extract bucketed sizes and sizes attribute
    const {bucketedSizes, sizesAttr} = useMemo(() => {
        const widthsWithFallback = widths || {base: DEFAULT_RESPONSIVE_IMAGE_WIDTH}

        return {
            bucketedSizes: getBucketedWidths({widths: widthsWithFallback, breakpoints}),
            sizesAttr: getSizesAttribute({widths: widthsWithFallback, breakpoints})
        }
    }, [widths])

    // Generate source elements
    const sourceElements = useMemo(() => {
        const elements = []
        const breakpointValues = sys.breakpoints.values || []
        const breakpointMap = Object.fromEntries(breakpointValues.map((bp) => [bp.name, bp.min]))

        // For each processed source (different breakpoints)
        processedSources.forEach((sourceObj) => {
            const {service, breakpoint} = sourceObj
            const mediaQuery =
                breakpoint && breakpoint !== 'base'
                    ? `(min-width: ${breakpointMap[breakpoint]})`
                    : null

            if (service) {
                // Generate sources from service
                const serviceSources = service.generateSources(bucketedSizes)

                // Add sizes attribute and media query to each source
                serviceSources.forEach((source) => {
                    elements.push({
                        ...source,
                        ...(sizesAttr && {sizes: sizesAttr}),
                        ...(mediaQuery && {media: mediaQuery})
                    })
                })
            }
        })

        return elements
    }, [processedSources, bucketedSizes, sizesAttr, sys.breakpoints.values])

    // Fallback img src - use direct URL to bypass service in case service is down
    const fallbackSrc = useMemo(() => {
        if (!mainService) return null

        // Use service's fallback URL (direct URL that bypasses service)
        // This ensures images still load if DIS or other service is down
        return mainService.getFallbackUrl()
    }, [mainService])

    // Determine if we should use picture element
    const usePictureElement = sourceElements.length > 0

    // All props go to img element (picture is just a semantic container)
    const imgProps = {
        alt,
        loading,
        ...(title && {title}),
        ...(fetchpriority && {fetchpriority}),
        ...rest
    }

    const imgElement = (
        <Box
            ref={composedImgRef}
            as="img"
            data-testid="dis-img"
            src={fallbackSrc || src}
            width="100%"
            height="100%"
            objectFit={objectFit}
            {...imgProps}
        />
    )

    // If not using DIS, render a plain img element
    if (!usePictureElement) {
        return imgElement
    }

    return (
        <Box as="picture" data-testid="dis-picture">
            {sourceElements.map((source, index) => (
                <source
                    key={index}
                    {...(source.type && {
                        'data-testid': `dis-source-${source.type.replace('image/', '')}`,
                        type: source.type
                    })}
                    srcSet={source.srcSet}
                    {...(source.sizes && {sizes: effectiveSizes(source.sizes)})}
                    {...(source.media && {media: source.media})}
                />
            ))}
            {imgElement}
        </Box>
    )
}

if (process.env.NODE_ENV === 'development') {
    DisImage.displayName = 'DisImage'
}

DisImage.propTypes = {
    src: PropTypes.string.isRequired,
    alt: PropTypes.string,
    title: PropTypes.string,
    loading: PropTypes.oneOf(['eager', 'lazy']),
    fetchpriority: PropTypes.oneOf(['high', 'low', 'auto']),
    widths: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    objectFit: PropTypes.oneOf(['contain', 'cover', 'fill', 'none', 'scale-down']),
    imgRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({current: PropTypes.any})]),
    sources: PropTypes.arrayOf(
        PropTypes.shape({
            src: PropTypes.string.isRequired,
            breakpoint: PropTypes.string
        })
    )
}

/**
 * Props comparison function for React.memo
 *
 * @private
 * @internal
 * @description Exported only for testing purposes. Do not use directly in application code.
 */
export const propsAreEqual = createPropsComparator({
    deepCompareProps: ['widths', 'sources']
})

export default React.memo(DisImage, propsAreEqual)
